import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../lib/AppContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';

export default function MessageThread({ userId }: { userId: string }) {
  const { currentUser } = useAppContext();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch recipient user info
  const { data: recipient } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, avatar')
        .eq('id', userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Fetch current user info (for avatar)
  const { data: currentUserInfo } = useQuery({
    queryKey: ['user', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      const { data, error } = await supabase
        .from('users')
        .select('id, name, avatar')
        .eq('id', currentUser.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser?.id,
  });

  // Fetch messages between current user and recipient
  const { data: messages, isLoading } = useQuery({
    queryKey: ['messages', currentUser?.id, userId],
    queryFn: async () => {
      if (!currentUser?.id || !userId) return [];
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${currentUser.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${currentUser.id})`)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id && !!userId,
    refetchInterval: 3000, // Poll for new messages every 3 seconds
  });

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      if (!currentUser?.id || !userId || !content.trim()) return;
      const { error } = await supabase
        .from('messages')
        .insert({ sender_id: currentUser.id, receiver_id: userId, content });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', currentUser?.id, userId] });
      setMessage('');
    },
  });

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      sendMessage.mutate(message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="flex flex-col flex-1 max-w-md mx-auto w-full h-screen">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-white/80 sticky top-0 z-10 relative">
          <button
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              const from = params.get('from');
              const userIdParam = params.get('user');
              if (from === 'profile' && (userIdParam || userId)) {
                const id = userIdParam || userId;
                window.location.assign(`/profile/${id}?from=messages`);
                return;
              }
              if (window.history.length > 1) {
                window.history.back();
              } else {
                window.location.assign('/messages');
              }
            }}
            className="absolute left-4 top-4 z-10 bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 text-blue-700" />
          </button>
          <div className="flex items-center justify-center w-full">
            <Avatar className="h-10 w-10">
              {recipient?.avatar ? (
                <AvatarImage src={recipient.avatar} alt={recipient.name} />
              ) : null}
              <AvatarFallback>{recipient?.name?.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
            </Avatar>
            <span className="font-semibold text-lg text-gray-900 ml-3">{recipient?.name || 'User'}</span>
          </div>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-gradient-to-br from-blue-50 via-white to-blue-100">
          {isLoading ? (
            <div className="text-center text-gray-400">Loading messages...</div>
          ) : (
            messages && messages.length > 0 ? (
              messages.map((msg: any, idx: number) => {
                const isMe = msg.sender_id === currentUser?.id;
                // Determine if this is the last message in a group from the recipient
                const isLastInGroup =
                  !isMe &&
                  (idx === messages.length - 1 || messages[idx + 1].sender_id !== msg.sender_id);
                return (
                  <div
                    key={msg.id}
                    className={`flex items-end ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* For received messages, always reserve avatar space on the left */}
                    {!isMe && (
                      isLastInGroup ? (
                        <Avatar className="h-7 w-7 mr-2">
                          {recipient?.avatar ? (
                            <AvatarImage src={recipient.avatar} alt={recipient.name} />
                          ) : null}
                          <AvatarFallback>{recipient?.name?.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-7 w-7 mr-2" style={{ visibility: 'hidden' }} />
                      )
                    )}
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 shadow text-sm ${isMe ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-900 rounded-bl-none border'}`}>
                      <div>{msg.content}</div>
                      <div className="text-xs text-gray-400 mt-1 text-right">
                        {format(new Date(msg.created_at), 'h:mm a')}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-400">No messages yet. Say hi!</div>
            )
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Message Input */}
        <form onSubmit={handleSend} className="flex items-center gap-2 px-4 py-3 pb-6 border-t bg-white/80 sticky bottom-0">
          <Input
            type="text"
            placeholder="Message..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            className="flex-1"
            autoComplete="off"
            disabled={sendMessage.status === 'pending'}
          />
          <Button type="submit" disabled={!message.trim() || sendMessage.status === 'pending'} className="bg-blue-600 text-white">
            Send
          </Button>
        </form>
      </div>
    </div>
  );
} 